<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- START OF FORM FIELDS -->
    <expansion-card
      icon="mdi-cancel"
      class="pb-1"
    >
      <span slot="title">Blocker</span>

      <v-flex class="px-3">
        <v-text-field
          v-model.trim="itemData.name"
          :label="type + ' Name'"
          :rules="rules().required('Name')"
          :error-messages="getPathErrors(jsonPath, 'name')"
          :disabled="isFormDisabled"
          required
          @input="clearPathErrors(jsonPath, 'name')"
        />
      </v-flex>

      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <date-picker
            v-model.trim="itemData.startDate"
            label="Start Date"
            :rules="rules().required()"
            required
            :error-messages="getPathErrors(jsonPath, 'startDate')"
            :disabled="isFormDisabled"
            @input="clearPathErrors(jsonPath, 'startDate'); clearDaySelectorErrors()"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <date-picker
            v-model.trim="blockerEndDate"
            label="End Date"
            :min="itemData.startDate"
            :rules="rules().required()"
            required
            :error-messages="getPathErrors(jsonPath, 'endDate')"
            :disabled="isFormDisabled"
            @input="clearPathErrors(jsonPath, 'endDate'); clearDaySelectorErrors()"
          />
        </v-flex>
      </v-layout>

      <v-flex class="px-3">
        <days-switch
          ref="dayPicker"
          v-model="itemData"
          label="Block Days"
          :disabled="isFormDisabled"
          :get-path-errors="getPathErrors"
          :clear-path-errors="clearPathErrors"
          :required="true"
        />
      </v-flex>

      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model="itemData.startTime"
            label="Start Time"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'startTime')"
            :disabled="isFormDisabled"
            required
            @input="clearPathErrors(jsonPath, 'startTime')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model="itemData.endTime"
            label="End Time"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'endTime')"
            :disabled="isFormDisabled"
            required
            @input="clearPathErrors(jsonPath, 'endTime')"
          />
        </v-flex>
      </v-layout>
    </expansion-card>

    <v-divider class="my-3" />

    <detail-editor-table
      v-model="itemData.locations"
      type="Location"
      :json-path="buildJsonPath('locations')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :disabled="isFormDisabled"
      :disable-add="!hasAvailLocation"
      :item-default="{
        id: null,
        locationId: null,
        blockerId: itemData.id
      }"
      :headers="[
        headerEditor({label: 'Location', field: 'locationId', editor: {type: 'slot'}})
      ]"
      @error="addError"
      @add="resetItemFieldErrorLookup"
    >
      <template
        slot="locationId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.locationId"
          placeholder="Select Location"
          :items="locations.filter((location) => {
            if (location.id === props.item.locationId) return true
            for (let i = 0, l = itemData.locations.length; i < l; i += 1) {
              if (itemData.locations[i].locationId === location.id) return false
            }
            return true
          })"
          :rules="rules().required()"
          required
          auto
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          @input="props.errorMessages._clear()"
        />
      </template>
    </detail-editor-table>

    <v-divider class="my-3" />

    <expansion-card
      icon="mdi-cog"
      class="pb-1"
    >
      <span slot="title">Options</span>

      <v-flex class="px-3">
        <v-select
          v-model="itemData.blockerType"
          prepend-icon="mdi-cog-box"
          label="Blocker Type"
          :items="blockerTypes"
        />
        <v-textarea
          v-model.trim="itemData.reason"
          label="Reason"
          :rows="1"
          auto-grow
          :rules="rules().required()"
          required
          :error-messages="getPathErrors(jsonPath, 'reason')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'reason')"
        />

        <v-boolean
          v-model="itemData.disabled"
          label="Enabled"
          invert
          :error-messages="getPathErrors(jsonPath, 'disabled')"
          :disabled="isFormDisabled"
        />
      </v-flex>
    </expansion-card>

    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import {values, itemEdit, withChildren} from '@/ft-vue-core'

const {daysShort, daysLower} = values

var itemDefault = {
  id: null,
  name: null,
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  reason: null,
  locations: [],
  disabled: false,
  blockerType: 'ALL',
  onlyBlockMemberMade: true,
  recordLockExpiration: null
}

export default {
  name: 'Blockers',
  components: {
  },
  mixins: [itemEdit, withChildren],
  data: function () {
    return {
      type: 'Blocker',
      types: 'Blockers',
      apiRecordCommand: 'diningBlocker',
      itemDefault: itemDefault,
      blockerTypes: [],
      locations: [],
      cloner: {
        id: null,
        locations: {
          _cloneMode: Array,
          id: null,
          blockerId: null
        }
      }
    }
  },
  computed: {
    blockerStartDate () {
      return this.itemData.startDate
    },
    blockerEndDate: {
      get () {
        return this.itemData.endDate || this.itemData.startDate
      },
      set (val) {
        this.itemData.endDate = val
      }
    },
    daysShort () {
      return daysShort
    },
    daysLower () {
      return daysLower
    },
    hasAvailLocation () {
      return this.locations.filter((location) => {
        if (location.disabled) return false
        if (!this.itemData.locations) return true
        for (let locBinding of this.itemData.locations) {
          if (locBinding.locationId === location.id) return false
        }
        return true
      }).length > 0
    }
  },
  watch: {
    blockerEndDate (val) {
      if (val < this.blockerStartDate) {
        this.itemData.endDate = this.itemData.startDate
      }
    },
    blockerStartDate (val) {
      if (!this.itemData.endDate || this.itemData.endDate < this.itemData.startDate) {
        this.itemData.endDate = this.itemData.startDate
      }
    }
  },
  methods: {
    hasAllDays (item) {
      for (let day of daysLower) {
        if (!item[day]) return false
      }
      return true
    },
    clearDaySelectorErrors () {
      this.$refs.dayPicker.clearDaySelectorErrors()
    },
    itemResourceCommands () {
      return [
        {
          command: 'apiFieldOption',
          load: this.blockerTypes,
          loading: this.isLoading,
          params: {filter_methods: {forField: ['diningBlocker', 'blockerType']}}
        },
        {command: 'diningLocation', load: this.locations, loading: this.isLoading}
      ]
    },
    listCommandFilter (command) {
      this.addCommandParamArrayVal(command, 'include_fields', 'locationNames', 'diningBlockerToLocation.locationName')
      return this.addCommandParam(command, 'children', true)
    }
  }
}
</script>
